import { Phone } from '@owner/icons-vue/Phone'
import { Star } from '@owner/icons-vue/Star'
import { computed, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import type { PlanComparisonSection, UpsellFeature } from '../types'

interface ProUpsellContent {
  description: ComputedRef<string>
  features: ComputedRef<UpsellFeature[]>
  plans: ComputedRef<string[]>
  price: ComputedRef<string>
  sections: ComputedRef<PlanComparisonSection[]>
  tableCaption: ComputedRef<string>
  title: ComputedRef<string>
}

const CARD_ICONS = {
  app: Phone,
  reviews: Star,
} as const

/**
 * Builds the shared plan-upgrade content rendered by the gated upsell surfaces.
 */
export function useProUpsellContent(): ProUpsellContent {
  const { t } = useI18n({ useScope: 'global' })

  const features = computed<UpsellFeature[]>(() =>
    (['app', 'reviews'] as const).map((key) => ({
      description: t(`upsell.feature-cards.${key}.body`),
      icon: CARD_ICONS[key],
      key,
      title: t(`upsell.feature-cards.${key}.title`),
    }))
  )

  const plans = computed(() => [t('upsell.plan-boost'), t('upsell.plan-pro')])

  const sections = computed<PlanComparisonSection[]>(() => [
    {
      rows: [
        {
          availability: [true, true],
          name: t('upsell.table-rows.website-improver'),
        },
        { availability: [true, true], name: t('upsell.table-rows.upscaling') },
        {
          availability: [true, true],
          name: t('upsell.table-rows.review-replies'),
        },
      ],
      title: t('upsell.table-sections.ai'),
    },
    {
      rows: [
        { availability: [true, true], name: t('upsell.table-rows.seo') },
        {
          availability: [true, true],
          name: t('upsell.table-rows.custom-website'),
        },
        { availability: [true, true], name: t('upsell.table-rows.auto-gbp') },
        { availability: [false, true], name: t('upsell.table-rows.job-board') },
        {
          availability: [false, true],
          name: t('upsell.table-rows.review-engine'),
        },
        { availability: [false, true], name: t('upsell.table-rows.listings') },
      ],
      title: t('upsell.table-sections.website'),
    },
    {
      rows: [
        {
          availability: [true, true],
          name: t('upsell.table-rows.online-ordering'),
        },
        {
          availability: [true, true],
          name: t('upsell.table-rows.order-with-google'),
        },
        {
          availability: [true, true],
          name: t('upsell.table-rows.live-tracking'),
        },
        {
          availability: [true, true],
          name: t('upsell.table-rows.delivery-support'),
        },
        {
          availability: [true, true],
          name: t('upsell.table-rows.catering-forms'),
        },
        {
          availability: [false, true],
          name: t('upsell.table-rows.reservations'),
        },
      ],
      title: t('upsell.table-sections.ordering'),
    },
    {
      rows: [
        { availability: [true, true], name: t('upsell.table-rows.auto-email') },
        {
          availability: [true, true],
          name: t('upsell.table-rows.custom-email'),
        },
        { availability: [true, true], name: t('upsell.table-rows.promotions') },
        { availability: [true, true], name: t('upsell.table-rows.reputation') },
        {
          availability: [true, true],
          name: t('upsell.table-rows.loyalty-integrations'),
        },
        {
          availability: [true, true],
          name: t('upsell.table-rows.unified-inbox'),
        },
        { availability: [false, true], name: t('upsell.table-rows.auto-sms') },
      ],
      title: t('upsell.table-sections.marketing'),
    },
    {
      rows: [
        {
          availability: [false, true],
          name: t('upsell.table-rows.branded-app'),
        },
        {
          availability: [false, true],
          name: t('upsell.table-rows.push-marketing'),
        },
        {
          availability: [false, true],
          name: t('upsell.table-rows.branded-gift-cards'),
        },
      ],
      title: t('upsell.table-sections.brand'),
    },
  ])

  return {
    description: computed(() => t('upsell.sheet-description')),
    features,
    plans,
    price: computed(() => t('upsell.price')),
    sections,
    tableCaption: computed(() => t('upsell.table-caption')),
    title: computed(() => t('upsell.title')),
  }
}
